/**
 * @fileoverview Utility functions for scene management, including room entity handling,
 * scene serialization, and design loading functionality. This module provides tools for
 * working with room hierarchies and handling saved designs in both Kvadrat and legacy formats.
 */
import { HierarchyUtils, SceneSerializationUtil, ComponentIds, TransformUtils } from '@inter-ikea-core/core';
import { KvadratVPCComponent, SerializationUtils, SnapBackResultType } from '@inter-ikea-core/core-toolbox';
import { CustomComponentIds, hasKvadratComponent } from '@inter-ikea-kvadrat/kvadrat';
/**
 * Returns all entities that are direct children of the room or that are non-room children
 * of the scene. It's meant to be used on the roots that are returned from deserialization.
 * It assumes that the room is marked with a `RoomComponent`.
 * This function has support for multi-entity rooms (where walls, windows, doors, etc.
 * are separate entities).
 *
 * @param roots - Entities returned from serialization
 * @returns All entities that are direct children of the room
 */
export function getChildrenOfRoomAndScene(roots) {
    return roots.flatMap((root) => {
        const room = findRoom(root);
        if (!room) {
            return [root];
        }
        else {
            const newRoots = [];
            const scene = HierarchyUtils.parent(room);
            if (scene) {
                const nonRoomChildren = HierarchyUtils.children(scene).filter((child) => !child.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
                newRoots.push(...nonRoomChildren);
            }
            const roomDescendants = findAllNonRoomPartDescendants(room);
            newRoots.push(...roomDescendants);
            return newRoots;
        }
    });
}
/**
 * Searches for a room entity within the provided scene hierarchy.
 *
 * @param scene - The root scene entity to search within
 * @returns The room entity if found, null otherwise
 */
function findRoom(scene) {
    return (HierarchyUtils.getDescendantWithComponent(scene, ComponentIds.ROOM_GEOMETRY_COMPONENT_ID) ??
        null);
}
/**
 * Determines if an entity is a room-related entity by checking for room-specific components.
 *
 * @param entity - The entity to check
 * @returns True if the entity is room-related, false otherwise
 */
function isRoomEntity(entity) {
    return (entity.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID) ||
        entity.hasComponent(ComponentIds.ROOM_COMPONENT_ID) ||
        entity.hasComponent(CustomComponentIds.ROOM_COMPONENT_ID) ||
        entity.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
}
/**
 * Recursively finds all descendants of a room part that are not room entities themselves.
 *
 * @param roomPart - The room part entity to search
 * @returns Array of non-room entities found in the hierarchy
 */
function findAllNonRoomPartDescendants(roomPart) {
    if (isRoomEntity(roomPart)) {
        return HierarchyUtils.children(roomPart).flatMap(child => findAllNonRoomPartDescendants(child));
    }
    else {
        return [roomPart];
    }
}
/**
 * Intercepts and transfers components from a room entity to the scene.
 * Used for backward compatibility with older storage formats where components
 * were stored on the room entity (ID range 2000-2100).
 *
 * @param coreServices - Core services instance
 * @param roomEntity - The room entity to process
 */
const oldComponentInterceptor = (coreServices, roomEntity) => {
    if (roomEntity) {
        roomEntity.components().forEach(component => {
            if (component.typeId >= 2000 && component.typeId <= 2100) {
                coreServices.sceneManager.scene.addComponent(component);
            }
        });
    }
};
/**
 * Loads a saved design into the scene, handling both Kvadrat and legacy formats.
 * This function performs the following steps:
 * 1. Deserializes the saved state
 * 2. Handles room setup (Kvadrat or legacy)
 * 3. Restores scene components
 * 4. Processes and places room children
 * 5. Snaps entities to their correct positions
 *
 * @param configuration - VPC configuration containing the saved design data
 * @param setRoom - Whether to set up the room during loading
 * @param coreServices - Core services instance
 * @param kvadratRoom - Kvadrat room API instance
 * @param placementService - Service for handling entity placement
 */
export const loadSavedDesign = async (configuration, setRoom, coreServices, kvadratRoom, placementService) => {
    const coreState = JSON.stringify({
        catalogs: configuration.catalogs,
        version: configuration.version,
        entities: configuration.entities
    });
    await loadPartialScene(coreState, setRoom, coreServices, kvadratRoom, placementService, configuration.kvadratVpc);
};
export const loadPartialScene = async (entitiesToLoadJson, setRoom, coreServices, kvadratRoom, placementService, kvadratVpc) => {
    const roots = coreServices.serializer.deserializeToEntityHierarchies(entitiesToLoadJson, coreServices);
    if (!roots || roots.length === 0) {
        // Early return if we failed to load the entities
        return;
    }
    let savedKvadratVpc = null;
    roots.forEach(root => {
        const holder = HierarchyUtils.getDescendantWithComponent(root, KvadratVPCComponent.id);
        if (holder) {
            //@ts-ignore
            savedKvadratVpc = holder.component.vpc;
        }
    });
    const roomVpc = savedKvadratVpc || kvadratVpc;
    if (setRoom) {
        if (roomVpc) {
            await kvadratRoom?.loadBlueprint(roomVpc);
        }
        else {
            // If this solution wasn't saved with kvadrat, we try to extract
            // the room dimensions from the room children and tell kvadrat to
            // create a room of that size.
            const roomDimensions = SerializationUtils.getRoomDimensionsFromLegacyComponents(roots);
            if (roomDimensions) {
                kvadratRoom?.setRoomSize(roomDimensions.size.width, roomDimensions.size.height, roomDimensions.size.depth);
            }
            else {
                // If we don't find any room dimensions, which is very unlikely, we just reset the room to the initial size
                kvadratRoom?.resetRoom();
            }
        }
    }
    // Restore components that where set on the scene entity
    const sceneEntity = SceneSerializationUtil.findSceneFromDeserializedEntityTrees(roots);
    let sceneProductChildren;
    if (sceneEntity) {
        // Restore any non-core components that were saved on the scene entity
        sceneEntity.components().forEach(component => {
            if (component.serializable) {
                coreServices.sceneManager.scene.addComponent(component);
            }
        });
        sceneProductChildren = HierarchyUtils.children(sceneEntity).filter(c => c.hasComponent(ComponentIds.PRODUCT_COMPONENT_ID));
    }
    // Older vpc solutions have components stored on the room entity.
    // As kvadrat removes this we need to extract all those components and add them
    // to the scene instead
    const oldRoomEntity = HierarchyUtils.hierarchy(roots[0]).find(entity => entity.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
    let roomChildren;
    // If we load a older vpc code with the old room component from core, we need to extract our custom components and add the to the scene
    if (oldRoomEntity) {
        oldComponentInterceptor(coreServices, oldRoomEntity);
        roomChildren = getChildrenOfRoomAndScene(roots);
    }
    else {
        // Get the entities that are children of the room in the loaded solution if the scene was saved with the new kvadrat room.
        roomChildren = roots.flatMap(root => {
            const roomParts = HierarchyUtils.descendants(root).filter(e => hasKvadratComponent(e));
            // If we can't any kvadrat room parts we just filter the children from the room
            // This case should not happen that often'
            if (!roomParts || roomParts.length === 0) {
                return getChildrenOfRoomAndScene(roots);
            }
            else {
                return roomParts.flatMap(part => HierarchyUtils.children(part).filter(p => !hasKvadratComponent(p)));
            }
        });
    }
    // Snap them into place.
    const snapToRoomResult = SerializationUtils.snapEntitiesToClosestPlace(roomChildren, coreServices.sceneManager, coreServices.validations);
    if (sceneProductChildren) {
        sceneProductChildren.forEach(c => {
            placementService.forcePlaceEntity(c);
        });
    }
    snapToRoomResult.forEach((result) => {
        switch (result.result) {
            case SnapBackResultType.INVALID_SNAP:
                // eslint-disable-next-line no-console
                console.log(`Entity ${result.entity.toString()} was snapped with validation errors.`);
                break;
            case SnapBackResultType.HAS_NO_CONNECTOR:
                // eslint-disable-next-line no-console
                console.log(`Entity ${result.entity.toString()} has no connector, attached it to the scene.`);
                break;
            case SnapBackResultType.NO_SNAP_FOUND:
                // eslint-disable-next-line no-console
                console.log(`Found no snap for entity ${result.entity.toString()}. Attached it to the scene.`);
                break;
            case SnapBackResultType.VALID_SNAP_WITH_MOVEMENT:
                // eslint-disable-next-line no-console
                console.log(`Found a snap for entity ${result.entity.toString()} by moving it to ${TransformUtils.worldTransform(result.entity)}.`);
                break;
            default:
                break;
        }
    });
};
//# sourceMappingURL=sceneUtils.js.map